import { c as _c } from "react/compiler-runtime";
import { useQueries } from "@tanstack/react-query";
import { DoseComponentFormValues } from "@/components/dose/dose-form/dose-form-schema";
import { getDoseInventoryMedicationId } from "@/components/dose/dose-form/edit-dose-model";
import { useDebounce } from "@/hooks/use-debounce";
import { ORAL_MEDICATION_NAMES } from "@/types/enums/weight-loss-medications";
import { Medication } from "@/types/medication";
import { DoseInventoryPreview, ShipmentLot, doseInventoryPreviewOptions, medicationsQueryOptions } from "@/query/medications/medications";
export interface DoseComponentInventoryEntry {
  selectedMedication?: Medication;
  shipments: ShipmentLot[];
  isCheckingInventory: boolean;
  preview?: DoseInventoryPreview;
  previewError?: boolean;
  /** The location the lots were scoped to — also scopes the off-inventory lot history. */
  locationId?: string | null;
  providerId?: string | null;
  patientId?: string | null;
  compoundMedicationId?: string | null;
}
type MedicationResolver = (component: DoseComponentFormValues, medications: Medication[], index: number) => Medication | undefined;
interface UseDoseComponentInventoryOptions {
  components: DoseComponentFormValues[];
  compoundMedicationId?: string | null;
  medications: Medication[];
  resolveMedication: MedicationResolver;
  currentLocationId?: string | null;
  /** The administering provider — scopes lots to their allocation when they have one. */
  providerId?: string | null;
  /** The patient being dosed — marks the lots their checkout already charged today. */
  patientId?: string | null;
  enabled: boolean;
  isPendingMedications?: boolean;
  /** The dose's location is still being looked up; lots wait for it. */
  isLocationPending?: boolean;
}
export function useDoseComponentInventory(t0) {
  const $ = _c(8);
  const {
    components,
    compoundMedicationId,
    medications,
    resolveMedication,
    currentLocationId,
    providerId,
    patientId,
    enabled,
    isPendingMedications: t1,
    isLocationPending: t2
  } = t0;
  const isPendingMedications = t1 === undefined ? false : t1;
  const isLocationPending = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== medications || $[1] !== resolveMedication) {
    t3 = (component, index) => medications.find(medication => medication.id === component.medicationId) ?? resolveMedication(component, medications, index);
    $[0] = medications;
    $[1] = resolveMedication;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const selectedMedications = components.map(t3);
  const inventoryMedicationIds = components.map((component_0, index_0) => getDoseInventoryMedicationId(component_0, selectedMedications[index_0]));
  let t4;
  if ($[3] !== components) {
    t4 = (medicationId, index_1) => {
      if (!medicationId || ORAL_MEDICATION_NAMES.includes(components[index_1].name)) {
        return;
      }
      const doseComponentId = components[index_1].id;
      const selection = components[index_1].lotSelection;
      return {
        key: `${medicationId}:${doseComponentId ?? ""}`,
        includeDepleted: components[index_1].includeDepletedMedicationId === medicationId,
        typedLot: selection?.type === "lot" ? selection.value.trim().toLowerCase() : undefined,
        medicationId,
        doseComponentId
      };
    };
    $[3] = components;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const inventoryRequestByComponent = inventoryMedicationIds.map(t4);
  const inventoryRequests = Array.from(new Map(inventoryRequestByComponent.flatMap(_temp)).values());
  const lotQueries = useQueries({
    queries: inventoryRequests.map(t5 => {
      const {
        medicationId: medicationId_0,
        doseComponentId: doseComponentId_0
      } = t5;
      return {
        ...medicationsQueryOptions.availableLots(medicationId_0, currentLocationId || undefined, providerId || undefined, doseComponentId_0, false, patientId || undefined),
        enabled: enabled && !isLocationPending
      };
    })
  });
  const depletedLotQueries = useQueries({
    queries: inventoryRequests.map(t6 => {
      const {
        key,
        medicationId: medicationId_1,
        doseComponentId: doseComponentId_1
      } = t6;
      return {
        ...medicationsQueryOptions.availableLots(medicationId_1, currentLocationId || undefined, providerId || undefined, doseComponentId_1, true, patientId || undefined),
        enabled: enabled && !isLocationPending && inventoryRequestByComponent.some(request_0 => request_0?.key === key && (request_0.includeDepleted || !!components.find(component_1 => component_1.medicationId === request_0.medicationId && component_1.lotAllocations?.length) || !!request_0.typedLot))
      };
    })
  });
  const numericInputs = components.map(_temp2);
  const debouncedNumericKey = useDebounce(JSON.stringify(numericInputs), 300);
  const requestedNumbers = JSON.parse(debouncedNumericKey) as typeof numericInputs;
  const numericPreviewPending = numericInputs.map((input, index_2) => input.amount !== requestedNumbers[index_2]?.amount || input.quantity !== requestedNumbers[index_2]?.quantity);
  const previewEnabled = components.map((component_3, index_3) => enabled && !isLocationPending && !!patientId && !!inventoryMedicationIds[index_3] && !ORAL_MEDICATION_NAMES.includes(component_3.name));
  const previewQueries = useQueries({
    queries: components.map((component_4, index_4) => ({
      ...doseInventoryPreviewOptions(inventoryMedicationIds[index_4] ?? "", {
        patientId: patientId ?? "",
        providerId,
        locationId: currentLocationId,
        doseComponentId: component_4.id,
        compoundMedicationId,
        amount: (requestedNumbers[index_4] ?? numericInputs[index_4]).amount,
        unit: component_4.unit,
        quantity: (requestedNumbers[index_4] ?? numericInputs[index_4]).quantity,
        dilutionId: component_4.dilutionId
      }),
      enabled: previewEnabled[index_4] && !numericPreviewPending[index_4]
    }))
  });
  const entries = components.map((component_5, index_5) => {
    const selectedMedication = selectedMedications[index_5];
    const inventoryRequest = inventoryRequestByComponent[index_5];
    const queryIndex = inventoryRequest ? inventoryRequests.findIndex(request_1 => request_1.key === inventoryRequest.key) : -1;
    const query = lotQueries[queryIndex];
    const depletedQuery = depletedLotQueries[queryIndex];
    const isMedicationLookupPending = enabled && isPendingMedications && !ORAL_MEDICATION_NAMES.includes(component_5.name) && !inventoryMedicationIds[index_5];
    const visibleById = new Map((query?.data ?? []).map(_temp3));
    for (const shipment_0 of depletedQuery?.data ?? []) {
      visibleById.set(shipment_0.id, shipment_0);
    }
    return {
      selectedMedication,
      shipments: isLocationPending ? [] : Array.from(visibleById.values()),
      preview: numericPreviewPending[index_5] ? undefined : previewQueries[index_5]?.data,
      previewError: numericPreviewPending[index_5] ? false : previewQueries[index_5]?.isError,
      isCheckingInventory: previewEnabled[index_5] && numericPreviewPending[index_5] || !!previewQueries[index_5]?.isFetching || isMedicationLookupPending || enabled && isLocationPending || !!query?.isFetching || !!depletedQuery?.isFetching,
      locationId: currentLocationId,
      providerId,
      patientId,
      compoundMedicationId
    };
  });
  const t7 = entries.some(_temp4);
  let t8;
  if ($[5] !== entries || $[6] !== t7) {
    t8 = {
      entries,
      isCheckingInventory: t7
    };
    $[5] = entries;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  return t8;
}
function _temp4(entry) {
  return entry.isCheckingInventory;
}
function _temp3(shipment) {
  return [shipment.id, shipment];
}
function _temp2(component_2) {
  return {
    amount: Number.isFinite(component_2.amount) ? component_2.amount : null,
    quantity: Number.isFinite(component_2.quantity) ? component_2.quantity : 1
  };
}
function _temp(request) {
  return request ? [[request.key, request] as const] : [];
}